import type {
  NearbyCityResponse,
  TeamClaimStatus,
  TeamVerificationMessageProps,
} from '@baseball-near-you/common';
import type {
  Address,
  AgeGroup,
  Facility,
  Grade,
  Question,
  Review,
  ReviewComment,
  ReviewLike,
  Staff,
  Tag,
  Team,
  TeamClaim,
  Update,
  User,
} from '@baseball-near-you/prisma';
import type { TeamCreationFormValues } from '@bb/components/Onboarding/steps';
import {
  AbstractClient,
  type PaginatedResponse,
  type PaginationQuery,
} from './AbstractFetchClient';

export class TeamClient extends AbstractClient {
  getAll = this.get<
    PaginatedResponse<
      (Team & {
        address: Address[];
        /** approved claims only */
        claims: TeamClaim[];
      })[]
    >,
    {
      query?: {
        lat?: number;
        long?: number;
        search?: string;
      } & PaginationQuery;
    }
  >('');

  getAllSlugs = this.get<string[], never>('/slugs');

  getAllNearby = this.get<
    PaginatedResponse<(Team & { address: Address[] })[]>,
    {
      query: {
        lat?: number;
        long?: number;
        search?: string;
        neLat?: number;
        neLng?: number;
        swLat?: number;
        swLng?: number;
      } & PaginationQuery;
    }
  >('/nearby');

  getAllClusters = this.get<
    PaginatedResponse<
      {
        clusterLat: number;
        clusterLng: number;
        teamCount: number;
        representativeTeamId: string;
      }[]
    >,
    {
      query: {
        zoom: number;
        neLat?: number;
        neLng?: number;
        swLat?: number;
        swLng?: number;
      };
    }
  >('/nearby/clusters');

  getCitiesNearby = this.get<
    NearbyCityResponse[],
    {
      query: { lat: number; long: number; limit?: number };
    }
  >('/nearby/cities');

  getSpecific = this.get<
    Team & {
      updates: Update[];
      staff: Staff[];
      address: Address[];
      facilities: (Facility & { address: Address })[];
      ageGroups: (AgeGroup & { address: Address; staff: Staff[] })[];
      /** approvedClaimsOnly */
      approvedClaims: { id: string; userId: number; status: TeamClaimStatus }[];
      reviews: Review[];
    },
    { query: { slug: string } }
  >('');

  create = this.payloadHandler<Team, { payload: TeamCreationFormValues }>(
    'POST',
    ''
  );

  update = this.payloadHandler<
    Team,
    {
      args: { id: number };
      payload: Partial<Team>;
    }
  >('PUT', ({ id }) => `/${id}`);

  addReview = this.payloadHandler<
    Review,
    { args: { teamId: number }; payload: Partial<Review> }
  >('POST', ({ teamId }) => `/${teamId}/reviews`);

  tagReview = this.payloadHandler<
    Review,
    { args: { teamId: number; reviewId: number }; payload: { tags: number[] } }
  >('POST', ({ teamId, reviewId }) => `/${teamId}/reviews/${reviewId}/tag`);

  claimTeam = this.payloadHandler<
    TeamClaim,
    {
      args: { teamId: number };
      payload: TeamVerificationMessageProps;
    }
  >('POST', ({ teamId }) => `/${teamId}/claims`);

  commentReview = this.payloadHandler<
    Review,
    {
      args: { teamId: number; reviewId: number };
      payload: Omit<ReviewComment, 'id' | 'createdAt'>;
    }
  >('POST', ({ teamId, reviewId }) => `/${teamId}/reviews/${reviewId}/comment`);

  likeReview = this.payloadHandler<
    Review,
    {
      args: { teamId: number; reviewId: number };
      payload: Omit<ReviewLike, 'createdAt' | 'id'>;
    }
  >('POST', ({ reviewId, teamId }) => `/${teamId}/reviews/${reviewId}/like`);

  unlikeReview = this.payloadHandler<
    Review,
    {
      args: { teamId: number; reviewId: number };
      payload: Pick<ReviewLike, 'id'>;
    }
  >('POST', ({ reviewId, teamId }) => `/${teamId}/reviews/${reviewId}/unlike`);

  getReviews = this.get<
    (Review & { user: User; tag: Tag[] })[],
    { args: { teamId: number } }
  >(({ teamId }) => `/${teamId}/reviews`);

  getReviewQuestions = this.get<Question[], Record<string, never>>(
    '/reviews/questions'
  );

  createReviewQuestion = this.payloadHandler<
    Question,
    { payload: Partial<Question> }
  >('POST', '/reviews/questions');

  getGrades = this.get<Grade[], { args: { teamId: number } }>(
    ({ teamId }) => `/${teamId}/grades`
  );

  addGrade = this.payloadHandler<
    Grade,
    { args: { teamId: number; reviewId: number }; payload: Partial<Grade> }
  >('POST', ({ teamId, reviewId }) => `/${teamId}/reviews/${reviewId}/grade`);

  getBulletins = this.get<
    Update[],
    {
      args: { teamId: number };
    }
  >(({ teamId }) => `/${teamId}/bulletin-board`);

  addBulletin = this.payloadHandler<
    Update,
    { args: { teamId: number }; payload: Partial<Update> }
  >('POST', ({ teamId }) => `/${teamId}/bulletin-board`);

  deleteBulletin = this.payloadHandler<
    Update,
    { args: { teamId: number; bulletinId: number } }
  >(
    'DELETE',
    ({ teamId, bulletinId }) => `/${teamId}/bulletin-board/${bulletinId}`
  );

  getStaff = this.get<
    Staff[],
    {
      args: { teamId: number };
    }
  >(({ teamId }) => `/${teamId}/staff`);

  addStaff = this.payloadHandler<
    Staff,
    { args: { teamId: number }; payload: Partial<Staff> }
  >('POST', ({ teamId }) => `/${teamId}/staff`);

  updateStaff = this.payloadHandler<
    Staff,
    {
      args: { teamId: number; staffId: number };
      payload: Partial<Staff>;
    }
  >('PUT', ({ teamId, staffId }) => `/${teamId}/staff/${staffId}`);

  getFacilities = this.get<
    Team & {
      address: Address[];
      facilities: (Facility & { address: Address })[];
    },
    {
      args: { teamId: number };
    }
  >(({ teamId }) => `/${teamId}/facilities`);

  getAgeGroups = this.get<
    (AgeGroup & { address: Address; staff: Staff[] })[],
    {
      args: { teamId: number };
    }
  >(({ teamId }) => `/${teamId}/age-group`);

  addAgeGroup = this.payloadHandler<
    AgeGroup[],
    {
      args: { teamId: number };
      payload: {
        teamId: number;
        newAgeGroups: Omit<AgeGroup, 'id' | 'addressId' | 'teamId'>[];
      };
    }
  >('POST', ({ teamId }) => `/${teamId}/age-group`);

  updateAgeGroup = this.payloadHandler<
    Team & {
      ageGroups: (AgeGroup & {
        address: Address;
        staff: Staff[];
      })[];
    },
    {
      args: { teamId: number; ageGroupId: number };
      payload: Partial<AgeGroup & { staff?: Pick<Staff, 'id'>[] }>;
    }
  >('PUT', ({ teamId, ageGroupId }) => `/${teamId}/age-group/${ageGroupId}`);
}
